import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';

const CinematicVideosPage = () => {
  const features = [
    {
      icon: 'ri-movie-2-line',
      title: 'AI Cinematography',
      description: 'Professional camera movements and framing powered by AI'
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Color Grading',
      description: 'Cinematic color palettes and mood enhancement'
    },
    {
      icon: 'ri-music-2-line',
      title: 'Sound Design',
      description: 'AI-generated soundscapes and audio enhancement'
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Turnaround',
      description: 'From concept to final cut in record time'
    },
    {
      icon: 'ri-4k-line',
      title: '4K & Beyond',
      description: 'Ultra-high definition output up to 8K resolution'
    },
    {
      icon: 'ri-scissors-cut-line',
      title: 'Smart Editing',
      description: 'AI-assisted editing for perfect pacing and flow'
    }
  ];

  const capabilities = [
    'Brand Films & Commercials',
    'Product Demonstrations',
    'Social Media Video Content',
    'Corporate Videos',
    'Music Videos',
    'Documentary Style Content'
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f010131811863b4d9e0a985c4e61cf15.jpg"
            alt="AI Video Generation"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-brand-aqua/20 to-brand-pink/20 backdrop-blur-xl border border-brand-aqua/30 mb-8">
              <i className="ri-film-line text-brand-aqua text-xl"></i>
              <span className="text-sm font-semibold text-white">AI Video Generation</span>
            </div>
            
            <h1 className="text-7xl font-light mb-8">
              <span className="text-white">Create </span>
              <span className="bg-gradient-to-r from-brand-aqua to-brand-pink bg-clip-text text-transparent">Cinematic</span>
              <br />
              <span className="text-white">Video Content</span>
            </h1>
            
            <p className="text-2xl text-white/80 leading-relaxed mb-12">
              Harness the power of AI to produce stunning video content with professional 
              cinematography, dynamic storytelling, and breathtaking visual effects.
            </p>

            <div className="flex gap-6">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-4 bg-gradient-to-r from-brand-teal to-brand-cyan rounded-full text-black font-bold whitespace-nowrap cursor-pointer"
                >
                  Get Started
                </motion.button>
              </Link>
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-4 border-2 border-white/20 text-white rounded-full font-semibold whitespace-nowrap cursor-pointer hover:border-brand-teal/50 transition-colors"
                >
                  All Services
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-32 bg-gradient-to-b from-transparent to-brand-teal/5">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-6xl font-light text-white mb-6">
              Cinematic Excellence
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Professional-grade video production powered by AI
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="p-8 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-white/10 cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-teal/20 to-brand-cyan/20 border border-brand-teal/30 mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${feature.icon} text-3xl text-brand-teal`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-white/60 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.05 }}
                    className="relative h-64 rounded-2xl overflow-hidden cursor-pointer"
                  >
                    <img
                      src={`https://readdy.ai/api/search-image?query=Cinematic%20video%20frame%20example%20with%20professional%20lighting%20and%20composition%2C%20teal%20cyan%20color%20grading%2C%20movie%20quality%20production%2C%20dramatic%20atmosphere%2C%208k%20quality&width=400&height=500&seq=video-ex-${i}&orientation=portrait`}
                      alt={`Example ${i}`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-6xl font-light text-white mb-8">
                What We Create
              </h2>
              <div className="space-y-4">
                {capabilities.map((capability, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-brand-teal/50 transition-colors cursor-pointer"
                  >
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-brand-teal to-brand-cyan" />
                    <span className="text-lg text-white/80">{capability}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-teal/10 to-brand-cyan/10 border border-brand-teal/20 p-20 text-center"
          >
            <h2 className="text-5xl font-light text-white mb-6">
              Ready to Tell Your Story?
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Let's create cinematic video content that captivates your audience.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-12 py-5 bg-gradient-to-r from-brand-teal to-brand-cyan rounded-full text-black font-bold text-lg whitespace-nowrap cursor-pointer"
              >
                Start Your Project
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CinematicVideosPage;